import { useState, useEffect, useMemo } from 'react';
import {
    ChevronRight,
    ChevronLeft,
    Check,
    FileText,
    Clock,
    Loader2,
    Upload,
    Eye,
    Search,
    Filter,
    Package,
    ArrowLeft,
    Store,
    RefreshCw,
    Image as ImageIcon2,
    ChevronUp,
    ChevronDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useShops } from '../contexts/ShopContext';
import { useAuth } from '../contexts/AuthContext';
import { getPrompts, Prompt } from '../services/promptService';
import { getNegativePrompt } from '../services/negativePromptService';
import { createAutomationJob, formatEstimatedTime, ProductRef } from '../services/automationService';
import { getCachedProducts, ShopifyProduct, syncShopProducts } from '../services/api';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Button, Input } from '../components/ui';
import './CreateAutomationPage.css';

type Step = 'products' | 'prompt' | 'confirm';
type StatusFilter = 'all' | 'active' | 'draft';

interface AIModel {
    id: string;
    name: string;
    provider: string;
    type: 'text' | 'image';
}

export default function CreateAutomationPage() {
    const navigate = useNavigate();
    const { shops, selectedShop, selectShop } = useShops();
    const { userProfile } = useAuth();

    const [currentStep, setCurrentStep] = useState<Step>('products');
    const [error, setError] = useState('');

    // Products state (from ProductsPage)
    const [products, setProducts] = useState<ShopifyProduct[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [hasImageFilter, setHasImageFilter] = useState(false);
    const [titleSort, setTitleSort] = useState<'none' | 'asc' | 'desc'>('none');
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

    // Selection state
    const [selectedProducts, setSelectedProducts] = useState<ProductRef[]>([]);
    const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

    // Prompt state
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [selectedPromptId, setSelectedPromptId] = useState('');
    const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
    const [negativePrompt, setNegativePrompt] = useState('');
    const [mode, setMode] = useState<'auto-upload' | 'review'>('review');
    const [isCreating, setIsCreating] = useState(false);

    // Model selection
    const [availableModels, setAvailableModels] = useState<AIModel[]>([]);
    const [modelId, setModelId] = useState('gemini-2.0-flash-exp-image-generation');
    const [isLoadingModels, setIsLoadingModels] = useState(true);

    // Context enrichment options
    const [useProductContext, setUseProductContext] = useState(true);
    const [useNegativePrompt, setUseNegativePrompt] = useState(true);

    // Load prompts
    useEffect(() => {
        if (userProfile?.uid) {
            getPrompts(userProfile.uid).then(setPrompts).catch(console.error);
        }
    }, [userProfile?.uid]);

    // Load AI models from Firestore
    useEffect(() => {
        const loadModels = async () => {
            try {
                const selectedModelsDoc = await getDoc(doc(db, 'settings', 'selected_models'));
                if (selectedModelsDoc.exists()) {
                    const data = selectedModelsDoc.data();
                    console.log('[CreateAutomationPage] selected_models data:', data);
                    const allModels: AIModel[] = [];

                    // Load Gemini models - prefer ModelsInfo, fallback to IDs
                    if (data.geminiModelsInfo && Array.isArray(data.geminiModelsInfo) && data.geminiModelsInfo.length > 0) {
                        for (const model of data.geminiModelsInfo) {
                            allModels.push({
                                id: model.id,
                                name: model.displayName || model.id,
                                provider: 'google',
                                type: model.type || (model.id.includes('image') ? 'image' : 'text'),
                            });
                        }
                    } else if (data.gemini && Array.isArray(data.gemini) && data.gemini.length > 0) {
                        // Fallback: use IDs directly when ModelsInfo is empty
                        console.log('[CreateAutomationPage] Using gemini IDs fallback:', data.gemini);
                        for (const modelId of data.gemini) {
                            allModels.push({
                                id: modelId,
                                name: modelId,
                                provider: 'google',
                                type: modelId.includes('image') ? 'image' : 'text',
                            });
                        }
                    }

                    // Load OpenAI models - prefer ModelsInfo, fallback to IDs
                    if (data.openaiModelsInfo && Array.isArray(data.openaiModelsInfo) && data.openaiModelsInfo.length > 0) {
                        for (const model of data.openaiModelsInfo) {
                            allModels.push({
                                id: model.id,
                                name: model.displayName || model.id,
                                provider: 'openai',
                                type: model.type || (model.id.includes('dall-e') ? 'image' : 'text'),
                            });
                        }
                    } else if (data.openai && Array.isArray(data.openai) && data.openai.length > 0) {
                        // Fallback: use IDs directly when ModelsInfo is empty
                        console.log('[CreateAutomationPage] Using openai IDs fallback:', data.openai);
                        for (const modelId of data.openai) {
                            allModels.push({
                                id: modelId,
                                name: modelId,
                                provider: 'openai',
                                type: modelId.includes('dall-e') || modelId.includes('gpt-image') ? 'image' : 'text',
                            });
                        }
                    }

                    console.log('[CreateAutomationPage] allModels:', allModels);
                    if (allModels.length > 0) {
                        setAvailableModels(allModels);
                        const savedModelId = localStorage.getItem('lastSelectedModel');
                        const savedModelAvailable = savedModelId && allModels.some(m => m.id === savedModelId);
                        if (savedModelAvailable) {
                            setModelId(savedModelId);
                        } else {
                            const imageModel = allModels.find(m => m.type === 'image');
                            setModelId(imageModel?.id || allModels[0].id);
                        }
                    }
                } else {
                    console.log('[CreateAutomationPage] selected_models document does not exist');
                }
            } catch (err) {
                console.error('Error loading models:', err);
            } finally {
                setIsLoadingModels(false);
            }
        };
        loadModels();
    }, []);

    // Real-time Firestore listener for products (exactly like ProductsPage)
    useEffect(() => {
        if (!selectedShop) {
            setProducts([]);
            return;
        }

        // Initial load from cache
        loadFromCache();

        // Set up real-time listener
        const productsRef = collection(db, 'products');
        const q = query(
            productsRef,
            where('shopId', '==', selectedShop.id),
            orderBy('shopifyCreatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const updatedProducts: ShopifyProduct[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: data.productId,
                    title: data.title,
                    handle: data.handle,
                    vendor: data.vendor,
                    product_type: data.product_type,
                    status: data.status,
                    tags: data.tags,
                    images: data.images || [],
                    variants: data.variants || [],
                };
            });
            setProducts(updatedProducts);
            setIsLoading(false);
        }, (err) => {
            console.error('Firestore listener error:', err);
            loadFromCache();
        });

        // Load negative prompt
        getNegativePrompt(selectedShop.id).then(np => {
            if (np) setNegativePrompt(np.content);
        });

        return () => unsubscribe();
    }, [selectedShop]);

    const loadFromCache = async () => {
        if (!selectedShop) return;

        setIsLoading(true);
        setError('');

        try {
            const result = await getCachedProducts(selectedShop.id);
            setProducts(result.products);
            setLastSyncedAt(result.lastSyncedAt);
        } catch (err: any) {
            console.error('Error loading products:', err);
            if (err.message?.includes('not found') || products.length === 0) {
                setError('Aucun produit en cache. Cliquez sur "Synchroniser" pour charger les produits depuis Shopify.');
            } else {
                setError(err.message || 'Erreur lors du chargement des produits');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSync = async () => {
        if (!selectedShop || isSyncing) return;

        setIsSyncing(true);
        setError('');

        try {
            const result = await syncShopProducts(selectedShop.id);
            setLastSyncedAt(new Date().toISOString());
            console.log(`Sync complete: ${result.syncedCount} products synced, ${result.deletedCount} deleted`);
        } catch (err: any) {
            console.error('Sync error:', err);
            setError(err.message || 'Erreur lors de la synchronisation');
        } finally {
            setIsSyncing(false);
        }
    };

    // Format last sync time (from ProductsPage)
    const formatLastSync = (isoDate: string | null): string => {
        if (!isoDate) return 'Jamais';
        const date = new Date(isoDate);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'À l\'instant';
        if (diffMins < 60) return `Il y a ${diffMins} min`;
        if (diffHours < 24) return `Il y a ${diffHours}h`;
        return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    };

    // Get SKU from first variant (from ProductsPage)
    const getProductSku = (product: ShopifyProduct): string => {
        if (product.variants && product.variants.length > 0 && product.variants[0].sku) {
            return product.variants[0].sku;
        }
        return '-';
    };

    // Filter products (from ProductsPage)
    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            // Has-image filter (AND condition) - only show products WITH images
            if (hasImageFilter) {
                if (!product.images || product.images.length === 0) {
                    return false;
                }
            }

            if (statusFilter !== 'all' && product.status?.toLowerCase() !== statusFilter) {
                return false;
            }
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const sku = getProductSku(product).toLowerCase();
                return (
                    product.title.toLowerCase().includes(query) ||
                    product.vendor?.toLowerCase().includes(query) ||
                    product.product_type?.toLowerCase().includes(query) ||
                    sku.includes(query)
                );
            }
            return true;
        });
    }, [products, statusFilter, searchQuery, hasImageFilter]);

    const sortedProducts = useMemo(() => {
        if (titleSort === 'none') return filteredProducts;
        return [...filteredProducts].sort((a, b) => {
            const cmp = a.title.localeCompare(b.title, 'fr');
            return titleSort === 'asc' ? cmp : -cmp;
        });
    }, [filteredProducts, titleSort]);

    // Counts for filters
    const activeCount = products.filter(p => p.status?.toLowerCase() === 'active').length;
    const draftCount = products.filter(p => p.status?.toLowerCase() === 'draft').length;
    const hasImageCount = products.filter(p => p.images && p.images.length > 0).length;

    // Selection handlers
    const isProductSelected = (productId: number) => {
        return selectedProducts.some(p => p.id === productId.toString());
    };

    const handleToggleProduct = (product: ShopifyProduct, event: React.MouseEvent, index: number) => {
        const productRef: ProductRef = {
            id: product.id.toString(),
            title: product.title,
            handle: product.handle,
            imageUrl: product.images?.[0]?.src
        };

        if (event.shiftKey && lastClickedIndex !== null) {
            // Shift+Click: sélectionner la plage de produits
            const start = Math.min(lastClickedIndex, index);
            const end = Math.max(lastClickedIndex, index);
            const rangeProducts = sortedProducts.slice(start, end + 1);

            const existingIds = new Set(selectedProducts.map(p => p.id));
            const newProducts = rangeProducts
                .filter(p => !existingIds.has(p.id.toString()))
                .map(p => ({
                    id: p.id.toString(),
                    title: p.title,
                    handle: p.handle,
                    imageUrl: p.images?.[0]?.src
                }));
            setSelectedProducts(prev => [...prev, ...newProducts]);
        } else {
            // Clic simple: toggle
            if (isProductSelected(product.id)) {
                setSelectedProducts(prev => prev.filter(p => p.id !== productRef.id));
            } else {
                setSelectedProducts(prev => [...prev, productRef]);
            }
        }

        setLastClickedIndex(index);
    };

    const handleSelectAllFiltered = () => {
        const allFilteredIds = new Set(filteredProducts.map(p => p.id.toString()));
        const allSelected = filteredProducts.every(p => isProductSelected(p.id));

        if (allSelected) {
            setSelectedProducts(prev => prev.filter(p => !allFilteredIds.has(p.id)));
        } else {
            const existingIds = new Set(selectedProducts.map(p => p.id));
            const newProducts = filteredProducts
                .filter(p => !existingIds.has(p.id.toString()))
                .map(p => ({
                    id: p.id.toString(),
                    title: p.title,
                    handle: p.handle,
                    imageUrl: p.images?.[0]?.src
                }));
            setSelectedProducts(prev => [...prev, ...newProducts]);
        }
    };

    const handleSelectPrompt = (prompt: Prompt) => {
        setSelectedPromptId(prompt.id);
        setSelectedPrompt(prompt);
    };

    const handleCreate = async () => {
        if (!userProfile?.uid || !selectedShop || !selectedPrompt || selectedProducts.length === 0) {
            return;
        }

        setIsCreating(true);
        setError('');

        try {
            const jobId = await createAutomationJob({
                userId: userProfile.uid,
                shopId: selectedShop.id,
                shopName: selectedShop.name,
                promptId: selectedPrompt.id,
                promptTitle: selectedPrompt.title,
                promptContent: selectedPrompt.content,
                negativePrompt: useNegativePrompt ? negativePrompt || undefined : undefined,
                modelId,
                mode,
                products: selectedProducts,
                createdBy: userProfile.email || userProfile.uid,
                useProductContext,
            });

            navigate(`/automations/${jobId}`);
        } catch (err: any) {
            console.error('Error creating automation:', err);
            setError(err.message || 'Erreur lors de la création');
        } finally {
            setIsCreating(false);
        }
    };

    const canProceed = () => {
        switch (currentStep) {
            case 'products': return selectedProducts.length > 0 && selectedShop;
            case 'prompt': return !!selectedPromptId;
            case 'confirm': return true;
            default: return false;
        }
    };

    const goNext = () => {
        const steps: Step[] = ['products', 'prompt', 'confirm'];
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex < steps.length - 1) {
            setCurrentStep(steps[currentIndex + 1]);
        }
    };

    const goBack = () => {
        const steps: Step[] = ['products', 'prompt', 'confirm'];
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex > 0) {
            setCurrentStep(steps[currentIndex - 1]);
        } else {
            navigate('/automations');
        }
    };

    const estimatedTime = selectedProducts.length * 15000;

    const allFilteredSelected = filteredProducts.length > 0 &&
        filteredProducts.every(p => isProductSelected(p.id));

    return (
        <div className="create-automation-page">
            {/* Header */}
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate('/automations')}>
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1>
                            <Package className="header-icon" />
                            Nouvelle automatisation
                        </h1>
                        <p>Sélectionnez les produits à traiter</p>
                    </div>
                </div>
            </header>

            {/* Progress Steps */}
            <div className="wizard-progress glass">
                {[
                    { key: 'products', label: 'Produits', icon: Package },
                    { key: 'prompt', label: 'Prompt & Options', icon: FileText },
                    { key: 'confirm', label: 'Confirmation', icon: Check },
                ].map((step, index) => {
                    const stepIndex = ['products', 'prompt', 'confirm'].indexOf(currentStep);
                    const isActive = currentStep === step.key;
                    const isCompleted = stepIndex > index;
                    return (
                        <div
                            key={step.key}
                            className={`wizard-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                        >
                            <div className="wizard-step-icon">
                                {isCompleted ? <Check size={16} /> : <step.icon size={16} />}
                            </div>
                            <span className="wizard-step-label">{step.label}</span>
                            {index < 2 && <div className="wizard-step-connector" />}
                        </div>
                    );
                })}
            </div>

            {/* Main Content */}
            <div className="wizard-content">
                {error && <div className="error-message glass">{error}</div>}

                {/* Step 1: Select Products (ProductsPage layout) */}
                {currentStep === 'products' && (
                    <div className="step-content products-step animate-fadeIn">
                        {/* Shop selector (exactly like ProductsPage) */}
                        {shops.length > 0 && (
                            <div className="shop-selector glass">
                                <label>Boutique :</label>
                                <select
                                    value={selectedShop?.id || ''}
                                    onChange={(e) => {
                                        const shop = shops.find(s => s.id === e.target.value);
                                        if (shop) selectShop(shop);
                                        setSelectedProducts([]); // Clear selection when shop changes
                                    }}
                                >
                                    <option value="">Sélectionner une boutique</option>
                                    {shops.map((shop) => (
                                        <option key={shop.id} value={shop.id}>
                                            {shop.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Toolbar with filters (exactly like ProductsPage) */}
                        {selectedShop && (
                            <div className="products-toolbar glass">
                                <Input
                                    placeholder="Rechercher par titre, SKU, vendeur..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    leftIcon={<Search size={18} />}
                                    className="search-input"
                                />
                                <div className="status-filters">
                                    <button
                                        className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                                        onClick={() => setStatusFilter('all')}
                                    >
                                        <Filter size={14} />
                                        Tous ({products.length})
                                    </button>
                                    <button
                                        className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
                                        onClick={() => setStatusFilter('active')}
                                    >
                                        Actifs ({activeCount})
                                    </button>
                                    <button
                                        className={`filter-btn ${statusFilter === 'draft' ? 'active' : ''}`}
                                        onClick={() => setStatusFilter('draft')}
                                    >
                                        Brouillons ({draftCount})
                                    </button>
                                    <button
                                        className={`filter-btn toggle ${hasImageFilter ? 'active' : ''}`}
                                        onClick={() => setHasImageFilter(!hasImageFilter)}
                                    >
                                        <ImageIcon2 size={14} />
                                        Avec image ({hasImageCount})
                                    </button>
                                </div>
                                <div className="products-count">
                                    {filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''} affiché{filteredProducts.length !== 1 ? 's' : ''}
                                </div>

                                {/* Sync section */}
                                <div className="sync-section">
                                    <span className="last-sync">
                                        <Clock size={14} />
                                        {formatLastSync(lastSyncedAt)}
                                    </span>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleSync}
                                        disabled={isSyncing}
                                        leftIcon={isSyncing ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                                    >
                                        {isSyncing ? 'Sync...' : 'Sync'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Selection action bar */}
                        {selectedShop && (
                            <div className="selection-bar glass">
                                <div className="selection-info">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleSelectAllFiltered}
                                    >
                                        {allFilteredSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                                    </Button>
                                    <span className="selection-count">
                                        <Check size={16} />
                                        {selectedProducts.length} produit{selectedProducts.length !== 1 ? 's' : ''} sélectionné{selectedProducts.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={goNext}
                                    disabled={!canProceed()}
                                    rightIcon={<ChevronRight size={18} />}
                                >
                                    Continuer
                                </Button>
                            </div>
                        )}

                        {/* Products table (exactly like ProductsPage) */}
                        {!selectedShop ? (
                            <div className="empty-state glass">
                                <Store size={64} className="empty-icon" />
                                <h2>Sélectionnez une boutique</h2>
                                <p>Choisissez une boutique Shopify pour voir ses produits.</p>
                            </div>
                        ) : isLoading ? (
                            <div className="loading-state glass">
                                <Loader2 className="spinner" size={48} />
                                <p>Chargement des produits...</p>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="empty-state glass">
                                <Package size={64} className="empty-icon" />
                                <h2>Aucun produit trouvé</h2>
                                <p>
                                    {searchQuery || statusFilter !== 'all'
                                        ? 'Aucun produit ne correspond à vos critères.'
                                        : 'Cette boutique ne contient pas encore de produits.'}
                                </p>
                                {products.length === 0 && (
                                    <Button variant="primary" onClick={handleSync} disabled={isSyncing}>
                                        {isSyncing ? 'Synchronisation...' : 'Synchroniser depuis Shopify'}
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="products-table-container glass">
                                <table className="products-table">
                                    <thead>
                                        <tr>
                                            <th className="col-checkbox"></th>
                                            <th className="col-images">Images</th>
                                            <th className="col-sku">SKU</th>
                                            <th className="col-title sortable-header" onClick={() => setTitleSort(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none')}>
                                                Titre
                                                {titleSort === 'asc' && <ChevronUp size={14} />}
                                                {titleSort === 'desc' && <ChevronDown size={14} />}
                                            </th>
                                            <th className="col-vendor">Vendeur</th>
                                            <th className="col-status">Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedProducts.map((product, index) => {
                                            const selected = isProductSelected(product.id);
                                            return (
                                                <tr
                                                    key={product.id}
                                                    className={selected ? 'selected' : ''}
                                                    onClick={(e) => handleToggleProduct(product, e, index)}
                                                >
                                                    <td className="col-checkbox">
                                                        <div className={`table-checkbox ${selected ? 'checked' : ''}`}>
                                                            {selected && <Check size={14} />}
                                                        </div>
                                                    </td>
                                                    <td className="col-images">
                                                        <div className="images-row">
                                                            {product.images && product.images.length > 0 ? (
                                                                [...product.images]
                                                                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                                                                    .map((image, index) => (
                                                                        <div key={image.id || index} className="image-wrapper">
                                                                            <img
                                                                                src={image.src}
                                                                                alt={image.alt || `${product.title} - image ${index + 1}`}
                                                                                className="product-thumbnail"
                                                                            />
                                                                        </div>
                                                                    ))
                                                            ) : (
                                                                <div className="no-image">
                                                                    <Package size={20} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="col-sku">
                                                        <span className="sku-text">{getProductSku(product)}</span>
                                                    </td>
                                                    <td className="col-title">
                                                        <span className="product-title">{product.title}</span>
                                                    </td>
                                                    <td className="col-vendor">
                                                        <span className="vendor-text">{product.vendor || '-'}</span>
                                                    </td>
                                                    <td className="col-status">
                                                        <span className={`status-badge status-${product.status?.toLowerCase() || 'draft'}`}>
                                                            {product.status === 'active' ? 'Actif' : 'Brouillon'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Select Prompt & Options */}
                {currentStep === 'prompt' && (
                    <div className="step-content animate-fadeIn">
                        <h2>Sélectionnez un prompt</h2>
                        <div className="prompts-list">
                            {prompts.map(prompt => (
                                <div
                                    key={prompt.id}
                                    className={`prompt-card glass ${selectedPromptId === prompt.id ? 'selected' : ''}`}
                                    onClick={() => handleSelectPrompt(prompt)}
                                >
                                    <div className="prompt-info">
                                        <h4>{prompt.title}</h4>
                                        <p>{prompt.content.substring(0, 150)}...</p>
                                    </div>
                                    {selectedPromptId === prompt.id && (
                                        <div className="selected-indicator">
                                            <Check size={18} />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Model Selection */}
                        <div className="options-section">
                            <h3>Modèle IA</h3>
                            {isLoadingModels ? (
                                <div className="loading-inline">
                                    <Loader2 size={16} className="spin" />
                                    <span>Chargement des modèles...</span>
                                </div>
                            ) : availableModels.length === 0 ? (
                                <div className="warning-message glass">
                                    ⚠️ Aucun modèle configuré. Configurez les modèles IA dans les Paramètres Admin.
                                </div>
                            ) : (
                                <div className="models-list">
                                    {availableModels.map((model) => (
                                        <div
                                            key={model.id}
                                            className={`model-card glass ${modelId === model.id ? 'selected' : ''}`}
                                            onClick={() => setModelId(model.id)}
                                        >
                                            <div className="model-info">
                                                <span className="model-name">{model.id}</span>
                                                <span className={`model-type ${model.type}`}>
                                                    {model.type === 'image' ? 'Image' : 'Texte'}
                                                </span>
                                            </div>
                                            {modelId === model.id && <Check size={16} />}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Mode Selection */}
                        <div className="options-section">
                            <h3>Mode de traitement</h3>
                            <div className="modes-grid">
                                <div
                                    className={`mode-card glass ${mode === 'review' ? 'selected' : ''}`}
                                    onClick={() => setMode('review')}
                                >
                                    <Eye size={28} />
                                    <div className="mode-info">
                                        <h4>Vérification manuelle</h4>
                                        <p>Validez les images avant upload</p>
                                    </div>
                                    {mode === 'review' && <Check size={18} className="check-icon" />}
                                </div>
                                <div
                                    className={`mode-card glass ${mode === 'auto-upload' ? 'selected' : ''}`}
                                    onClick={() => setMode('auto-upload')}
                                >
                                    <Upload size={28} />
                                    <div className="mode-info">
                                        <h4>Upload automatique</h4>
                                        <p>Upload direct sur Shopify</p>
                                    </div>
                                    {mode === 'auto-upload' && <Check size={18} className="check-icon" />}
                                </div>
                            </div>
                        </div>

                        {/* Context Options */}
                        <div className="options-section">
                            <h3>Options</h3>
                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={useProductContext}
                                    onChange={(e) => setUseProductContext(e.target.checked)}
                                />
                                <span>Enrichir avec le contexte du produit</span>
                                <span className="hint">(titre, description, tags)</span>
                            </label>

                            {negativePrompt && (
                                <label className="checkbox-option">
                                    <input
                                        type="checkbox"
                                        checked={useNegativePrompt}
                                        onChange={(e) => setUseNegativePrompt(e.target.checked)}
                                    />
                                    <span>Appliquer le prompt négatif</span>
                                    <span className="hint">({negativePrompt.substring(0, 40)}...)</span>
                                </label>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 3: Confirm */}
                {currentStep === 'confirm' && (
                    <div className="step-content animate-fadeIn">
                        <h2>Récapitulatif</h2>
                        <div className="summary-card glass">
                            <div className="summary-row">
                                <span className="label">
                                    <Store size={16} />
                                    Boutique
                                </span>
                                <span className="value">{selectedShop?.name}</span>
                            </div>
                            <div className="summary-row">
                                <span className="label">
                                    <Package size={16} />
                                    Produits
                                </span>
                                <span className="value">{selectedProducts.length} produit(s)</span>
                            </div>
                            <div className="summary-row">
                                <span className="label">
                                    <FileText size={16} />
                                    Prompt
                                </span>
                                <span className="value">{selectedPrompt?.title}</span>
                            </div>
                            <div className="summary-row">
                                <span className="label">
                                    {mode === 'review' ? <Eye size={16} /> : <Upload size={16} />}
                                    Mode
                                </span>
                                <span className="value">{mode === 'auto-upload' ? 'Upload auto' : 'Vérification manuelle'}</span>
                            </div>
                            <div className="summary-row highlight">
                                <span className="label">
                                    <Clock size={16} />
                                    Temps estimé
                                </span>
                                <span className="value">{formatEstimatedTime(estimatedTime)}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer Navigation (always visible) */}
            <div className="wizard-footer glass">
                <Button variant="ghost" onClick={goBack} disabled={isCreating}>
                    <ChevronLeft size={18} />
                    {currentStep === 'products' ? 'Annuler' : 'Retour'}
                </Button>
                <div className="spacer" />
                {currentStep !== 'confirm' ? (
                    <Button
                        variant="primary"
                        onClick={goNext}
                        disabled={!canProceed()}
                        rightIcon={<ChevronRight size={18} />}
                    >
                        {currentStep === 'products'
                            ? `Continuer (${selectedProducts.length} produit${selectedProducts.length !== 1 ? 's' : ''})`
                            : 'Continuer'}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        onClick={handleCreate}
                        disabled={isCreating}
                        leftIcon={isCreating ? <Loader2 size={18} className="spin" /> : undefined}
                    >
                        {isCreating ? 'Création...' : 'Lancer l\'automatisation'}
                    </Button>
                )}
            </div>
        </div>
    );
}
